import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "react-use";
import { addYears, startOfDay } from "date-fns";
import { Box } from "@mui/material";
import {
  AccountsApiListAndSearchAllAccountsRequest,
  AccountsApiSelectGroupsIdRequest,
} from "@rentablo/api/apiv1";
import { useListAndSearchAllAccounts } from "@rentablo/api/apiv1";
import { CardPageLayout, CardHeading } from "@rentablo/components";
import { InvestmentsActions } from "@features/investments/components/InvestmentsActions";
import { InvestmentsPageMobilePanel } from "@features/investments/components/InvestmentsPageMobilePanel";
import { InvestmentsPageTitle } from "@features/investments/components/InvestmentsPageTitle";
import {
  CurrencyToggle,
  useCurrencyChanged,
  useCurrencyToggleStorage,
} from "@components";
import { InvestmentsChartDataStatistic } from "@api/v1/hooks/performance/useInvestmentsChartData.ts";
import { MandatorConditional, useMandatorVisibility } from "@helpers";
import { useAccountIdsByTypes, useRangeSelectControl } from "@hooks";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { ChartControlsProvider } from "@components/Chart/ChartControls";
import { AssetTypeBreakdown } from "../depots/components/DepotsDashboard/AssetTypeBreakdown";
import {
  DepotsDashboardChart,
  DepotsDashboardContent,
} from "../depots/components/DepotsDashboard";
import { AssetClassFilterProvider } from "../depots/components/DepotsDashboard/assetClassFilter";
import { useSelectGroupsForId } from "../../hooks/useSelectGroupsForId.ts";

const accountParams: AccountsApiListAndSearchAllAccountsRequest = {
  type: ["01_depot", "05_material_assets"],
};

const selectedGroupParams: AccountsApiSelectGroupsIdRequest = {
  id: "investmentList",
};

const CURRENCY_STORAGE_KEY = "currency-toggle-investments-list";

export const InvestmentsPage = () => {
  const { t } = useTranslation();
  const isComparisonSelectorVisible = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });
  // The asset class cards filter the whole page, chart included; without them
  // (kpmg-germany) the chart keeps its own asset type checkboxes.
  const showAssetClassCards = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });
  const bookingsRangeProps = useRangeSelectControl({
    firstDate: new Date(),
    id: "investments-bookings",
  });
  const { globalCurrency, selectedCurrencyView } = useCurrencyDisplay();
  const [statistic, setStatistic] = useState<InvestmentsChartDataStatistic>();
  const [showHistoric = false, setShowHistoric] = useLocalStorage(
    "show-historic-investments",
    false
  );
  const currencyChanged = useCurrencyChanged();
  const { onChange: setCurrencyView } =
    useCurrencyToggleStorage(CURRENCY_STORAGE_KEY);

  const { data: selectedGroup, refetch: refetchGroups } = useSelectGroupsForId({
    id: selectedGroupParams.id,
    accountParams,
  });
  const { data: accounts } = useListAndSearchAllAccounts(accountParams);

  const selectedAccounts = useMemo(
    () =>
      accounts?.accounts?.filter((account) =>
        selectedGroup?.accountIds?.includes(account.id)
      ),
    [accounts, selectedGroup?.accountIds]
  );
  const groupedAccountIds = useAccountIdsByTypes(selectedAccounts);
  const depotAccountIds = useMemo(
    () => groupedAccountIds.depotIds ?? [],
    [groupedAccountIds.depotIds]
  );
  const investmentsChartFirstDate = startOfDay(addYears(new Date(), -1));

  const handleShowHistoricChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setShowHistoric(event.target.checked);

  return (
    <AssetClassFilterProvider storageKey="asset-class-filter-investments">
      <CardPageLayout
        id="layout"
        data-testid="investments-page"
        hasBottomControlsPanel
        breadcrumbs={[
          { to: "/", text: t("breadcrumbs.home") },
          { text: t("breadcrumbs.investments") },
        ]}
        cardHeading={
          <CardHeading
            title={
              <InvestmentsPageTitle
                selectedGroup={selectedGroup}
                accountParams={accountParams}
                selectedGroupParams={selectedGroupParams}
                onSave={() => refetchGroups()}
              />
            }
            actions={<InvestmentsActions />}
          />
        }
        cardChildren={
          <ChartControlsProvider
            rangeId="investments"
            rangeFirstDate={investmentsChartFirstDate}
            controls={{
              showChartType: true,
              showCompare: isComparisonSelectorVisible,
              showAssetTypes: !showAssetClassCards,
            }}
          >
            <Box>
              <DepotsDashboardChart
                accountIds={depotAccountIds}
                onStatistic={setStatistic}
                currency={globalCurrency}
              />
            </Box>
            <InvestmentsPageMobilePanel />
          </ChartControlsProvider>
        }
      >
        <MandatorConditional hideFor={["kpmg-germany"]}>
          <AssetTypeBreakdown
            accountIds={depotAccountIds}
            currency={globalCurrency}
          />
        </MandatorConditional>
        {statistic && (
          <DepotsDashboardContent
            accountIds={depotAccountIds}
            statistic={statistic}
            currency={
              selectedCurrencyView === "global" ? globalCurrency : undefined
            }
            summaryCurrency={globalCurrency}
            showHistoric={showHistoric}
            onShowHistoricChange={handleShowHistoricChange}
            bookingsRangeProps={bookingsRangeProps}
            mobileSettingsExtra={
              <CurrencyToggle
                storageKey={CURRENCY_STORAGE_KEY}
                variant="buttons"
                fullWidth
                color="textPrimary"
              />
            }
            mobileSettingsChanged={currencyChanged}
            onMobileSettingsReset={(tab) => {
              setCurrencyView("original");
              if (tab === "investments") setShowHistoric(false);
              else
                bookingsRangeProps.selectInterval(
                  bookingsRangeProps.defaultIntervalType
                );
            }}
          />
        )}
      </CardPageLayout>
    </AssetClassFilterProvider>
  );
};
